import { createRoute, lazyRouteComponent, stripSearchParams } from '@tanstack/react-router'
import type { AppointmentRole, RegisterSort, RegisterStatus } from '@volee/api-client'

import { requireBackoffice } from '../auth/guard.ts'
import { APPOINTMENT_ROLE_ORDER } from '../messages/index.ts'
import { parseOneOf, parsePage, parseQuery } from './params.ts'
import { rootRoute } from './root.tsx'

// Every incarico across every club: «who has ended and is nowhere any more» is `status=former`,
// which one club cannot answer. The endpoint also filters by club, and no screen produces that.

export interface RegisterSearch {
  /** 1-based: it is the number a person shares. */
  readonly page: number
  readonly q: string | null
  readonly role: AppointmentRole | null
  readonly status: RegisterStatus
  readonly sort: RegisterSort
}

const SORTS: readonly RegisterSort[] = ['last_name', '-last_name']
const STATUSES: readonly RegisterStatus[] = ['active', 'ended', 'former', 'all']

export const DEFAULT_REGISTER_SORT: RegisterSort = 'last_name'
export const DEFAULT_REGISTER_STATUS: RegisterStatus = 'active'

export const DEFAULT_REGISTER_SEARCH: RegisterSearch = {
  page: 1,
  q: null,
  role: null,
  status: DEFAULT_REGISTER_STATUS,
  sort: DEFAULT_REGISTER_SORT,
}

export function parseRegisterSort(value: unknown): RegisterSort {
  return parseOneOf(SORTS, value) ?? DEFAULT_REGISTER_SORT
}

export function parseRegisterRole(value: unknown): AppointmentRole | null {
  return parseOneOf(APPOINTMENT_ROLE_ORDER, value)
}

export function parseRegisterStatus(value: unknown): RegisterStatus {
  return parseOneOf(STATUSES, value) ?? DEFAULT_REGISTER_STATUS
}

/** `all` is the absence of a state filter, not a stricter one: it must not be counted as one,
 * and clearing must not turn it back into the default. */
function isStateFilter(status: RegisterStatus): boolean {
  return status !== DEFAULT_REGISTER_STATUS && status !== 'all'
}

/** One owner for «which of these params is a filter»: the page asked it twice, once to draw
 * the empty state and once to clear, and the two answers disagreed. */
export function hasFilters(search: RegisterSearch): boolean {
  return search.q !== null || search.role !== null || isStateFilter(search.status)
}

export function withoutFilters(search: RegisterSearch): Partial<RegisterSearch> {
  return {
    q: null,
    role: null,
    status: isStateFilter(search.status) ? DEFAULT_REGISTER_STATUS : search.status,
  }
}

/** The register as a link target: path and the search params its type requires. */
export const appointmentListLink = {
  to: '/appointments',
  search: DEFAULT_REGISTER_SEARCH,
} as const

export const appointmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/appointments',
  validateSearch: (search: Record<string, unknown>): RegisterSearch => ({
    page: parsePage(search.page),
    q: parseQuery(search.q),
    role: parseRegisterRole(search.role),
    status: parseRegisterStatus(search.status),
    sort: parseRegisterSort(search.sort),
  }),
  search: { middlewares: [stripSearchParams(DEFAULT_REGISTER_SEARCH)] },
  beforeLoad: requireBackoffice,
  component: lazyRouteComponent(() => import('../pages/appointments.tsx')),
})
